/**
 * 通話料の実績(アジャストワンの請求明細CSV)を PL で使う形にする(2026-09-28 発注者指示)。
 *
 *  - **人が確認して「PLに反映する」を押した月だけ**使う(`month_close/{月}.calls.applied`)。自動では変わらない
 *  - **会社に紐づく番号 → その会社の通話料**、**会社の無い番号 → 「その他(CC事業部以外)」**
 *  - 同じ会社に複数の番号があるとき(例 サウスポートの架電用・受電用)は**用途ごとの内訳**も出せる
 *  - 反映していない月は、これまでどおり **通話時間(分) × 単価** の推定を使う
 */
import type { DateStr, Yen } from '@core/lib/types';
import type { CallNumber } from '@core/masters/settingsPayRepo';
import type { MonthCallCharges } from './monthCloseRepo';

const digits = (s: string) => String(s ?? '').replace(/[^0-9]/g, '');

export type CallActuals = {
  /** 実績を使う月(YYYY-MM)。ここに無い月は推定のまま */
  months: Set<string>;
  /** `${company_id}|${date}` → 円 */
  byCompanyDate: Map<string, Yen>;
  /** `${number}|${date}` → 円(会社の中の用途別に使う) */
  byNumberDate: Map<string, Yen>;
  /** `${date}` → 円(CC事業部以外の合計) */
  otherByDate: Map<string, Yen>;
  /** 会社 → その会社の番号(実績に出てきたもの) */
  numbersOf: Map<string, string[]>;
  /** CC事業部以外の番号 */
  otherNumbers: string[];
  /** 番号 → 用途(設定の label。無ければ番号そのまま) */
  labelOf: (number: string) => string;
};

export function emptyCallActuals(): CallActuals {
  return {
    months: new Set(),
    byCompanyDate: new Map(),
    byNumberDate: new Map(),
    otherByDate: new Map(),
    numbersOf: new Map(),
    otherNumbers: [],
    labelOf: (n) => n,
  };
}

/** 見やすい番号(0120924683 → 0120-92-4683 のようには変えず、設定に入っている表記を使う) */
function displayOf(numbers: CallNumber[], number: string): string {
  const hit = numbers.find((x) => digits(x.number) === digits(number));
  return hit?.number ?? number;
}

/** 確定した月の実績 ＋ 発信番号の設定 → PLで使う形 */
export function buildCallActuals(applied: { month: string; calls: MonthCallCharges }[], numbers: CallNumber[]): CallActuals {
  const a = emptyCallActuals();
  const labels = new Map<string, string>();
  for (const n of numbers) if (digits(n.number)) labels.set(digits(n.number), n.label || '');
  for (const { month, calls } of applied) {
    a.months.add(month);
    for (const r of calls.rows ?? []) {
      const num = digits(r.number ?? '');
      // 丸めずに足して、表示のときに丸める(③の画面と同じ金額になるように)
      const yen = r.yen;
      if (r.company_id) {
        const key = `${r.company_id}|${r.date}`;
        a.byCompanyDate.set(key, (a.byCompanyDate.get(key) ?? 0) + yen);
        const list = a.numbersOf.get(r.company_id) ?? [];
        if (num && !list.includes(num)) a.numbersOf.set(r.company_id, [...list, num]);
      } else {
        a.otherByDate.set(r.date, (a.otherByDate.get(r.date) ?? 0) + yen);
        if (num && !a.otherNumbers.includes(num)) a.otherNumbers.push(num);
      }
      if (num) {
        const nk = `${num}|${r.date}`;
        a.byNumberDate.set(nk, (a.byNumberDate.get(nk) ?? 0) + yen);
      }
    }
  }
  a.labelOf = (n: string) => labels.get(digits(n)) || displayOf(numbers, n);
  return a;
}

/** その日その会社の通話料。実績を使う月なら実績(無ければ0)、そうでなければ null(＝推定を使う) */
export function actualCallFee(a: CallActuals, companyId: string, date: DateStr): Yen | null {
  if (!a.months.has(date.slice(0, 7))) return null;
  return a.byCompanyDate.get(`${companyId}|${date}`) ?? 0;
}

/** その日の「その他(CC事業部以外)」。実績を使う月でなければ null */
export function otherCallFee(a: CallActuals, date: DateStr): Yen | null {
  if (!a.months.has(date.slice(0, 7))) return null;
  return a.otherByDate.get(date) ?? 0;
}

/** その日その番号の通話料(会社の中の用途別)。実績を使う月でなければ null */
export function numberCallFee(a: CallActuals, number: string, date: DateStr): Yen | null {
  if (!a.months.has(date.slice(0, 7))) return null;
  return a.byNumberDate.get(`${digits(number)}|${date}`) ?? 0;
}

/** 期間のうち、実績を使う日が1日でもあるか */
export function hasActuals(a: CallActuals, dates: DateStr[]): boolean {
  return dates.some((d) => a.months.has(d.slice(0, 7)));
}
